import React, { createContext, useContext, useState, useEffect } from 'react';

// Wood theme configurations
export const woodStyles = {
  dark: {
    background: '#121212', // Deep dark background
    backgroundImage: `
      linear-gradient(to bottom, #1E1E1E 0%, #121212 100%)
    `,
    boxShadow: '0 2px 4px rgba(0,0,0,0.3), 0 -2px 4px rgba(255,255,255,0.05)',
    borderColor: '#333333',
    surface: {
      primary: 'bg-[#1E1E1E]/90', // Dark gray surface
      secondary: 'bg-[#2C2C2C]/80', // Slightly lighter dark gray
      accent: 'bg-[#383838]/70' // Accent dark gray
    },
    text: {
      primary: 'text-gray-100',
      secondary: 'text-gray-300',
      accent: 'text-gray-400'
    },
    button: {
      primary: 'bg-[#2E8B57] hover:bg-[#3CB371]', // Sea Green
      secondary: 'bg-[#4A4A4A] hover:bg-[#5A5A5A]', // Dark gray
      disabled: 'bg-gray-700 cursor-not-allowed',
      success: 'bg-[#22C55E] hover:bg-[#16A34A]'
    },
    inlay: {
      background: 'bg-[#1A1A1A]', // Very dark background
      shadow: 'shadow-[inset_0_1px_3px_rgba(0,0,0,0.5)]',
      overlay: 'before:bg-black/20',

    },
    card: {
      background: 'bg-[#1E1E1E]',
      border: 'border-[#333333]',
      hover: 'hover:bg-[#2A2A2A]'
    }
  },
  light: {
    background: '#F0F0F0', // Light gray background
    backgroundImage: `
      linear-gradient(to bottom, #FFFFFF 0%, #F0F0F0 100%)
    `,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    borderColor: '#0078D7', // XP Blue for borders in light mode
    surface: {
      primary: 'bg-[#E1E1E1]/80', // Light Gray
      secondary: 'bg-[#FFFFFF]/90', // White
      accent: 'bg-[#0078D7]/20' // XP Blue accent
    },
    text: {
      primary: 'text-black',
      secondary: 'text-gray-700',
      accent: 'text-[#0078D7]' // XP Blue for accents
    },
    button: {
      primary: 'bg-[#0078D7] hover:bg-[#1E90FF]', // XP Blue
      secondary: 'bg-[#708090] hover:bg-[#778899]', // Slate Gray
      disabled: 'bg-gray-400 cursor-not-allowed',
      success: 'bg-[#22C55E] hover:bg-[#16A34A]'
    },
    inlay: {
      background: 'bg-[#FFFFFF]', // White
      shadow: 'shadow-[inset_0_1px_3px_rgba(0,0,0,0.1)]',
      overlay: 'before:bg-black/5'
    },
    card: {
      background: 'bg-white',
      border: 'border-[#0078D7]/20', // XP Blue border
      hover: 'hover:bg-gray-50'
    }
  }
};

export const getSlotColors = (woodStyles) => ({
  0: {
    base: 'rgb(59, 130, 246, 0.3)',
    active: 'rgb(59, 130, 246)',
    text: woodStyles.text.primary,
    ghostColor: 'rgba(59, 130, 246, 0.5)',
    glow: '0 0 10px rgb(59, 130, 246), 0 0 20px rgb(59, 130, 246)',
    selected: woodStyles.button.primary
  },
  1: {
    base: 'rgb(34, 197, 94, 0.3)',
    active: 'rgb(34, 197, 94)',
    text: woodStyles.text.primary,
    ghostColor: 'rgba(34, 197, 94, 0.5)',
    glow: '0 0 10px rgb(34, 197, 94), 0 0 20px rgb(34, 197, 94)',
    selected: woodStyles.button.primary
  },
  2: {
    base: 'rgb(168, 85, 247, 0.3)',
    active: 'rgb(168, 85, 247)',
    text: woodStyles.text.primary,
    ghostColor: 'rgba(168, 85, 247, 0.5)',
    glow: '0 0 10px rgb(168, 85, 247), 0 0 20px rgb(168, 85, 247)',
    selected: woodStyles.button.primary
  }
});
// Theme storage key
const THEME_STORAGE_KEY = 'chess-theme-preference';

// Get initial theme from storage or system preference
const getInitialTheme = () => {
  const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
  if (savedTheme !== null) {
    return JSON.parse(savedTheme);
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

const ThemeContext = createContext({
  isDark: true,
  toggleTheme: () => { },
  woodStyles: woodStyles.dark
});

export const useTheme = () => useContext(ThemeContext);


export const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(getInitialTheme);

  // Effect to sync theme with localStorage and apply to document
  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(isDark));
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  // Effect to listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      if (!localStorage.getItem(THEME_STORAGE_KEY)) {
        setIsDark(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    setIsDark(prev => !prev);
  };

  const currentStyles = isDark ? woodStyles.dark : woodStyles.light;

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme, woodStyles: currentStyles }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Reusable themed components
export const ThemedCard = ({ children, className = '', onClick }) => {
  const { woodStyles } = useTheme();

  return (
    <div
      onClick={onClick}
      className={`rounded-lg border-2 shadow-xl overflow-hidden transition-all duration-200
        ${woodStyles.card.background} ${woodStyles.card.border} ${woodStyles.card.hover} ${className}`}
      style={{
        background: woodStyles.background,
        backgroundImage: woodStyles.backgroundImage,
        boxShadow: woodStyles.boxShadow
      }}
    >
      {children}
    </div>
  );
};

export const ThemedInlay = ({ children, className = '' }) => {
  const { woodStyles } = useTheme();

  return (
    <div className={`relative rounded-lg overflow-hidden ${className}
      ${woodStyles.inlay.background} ${woodStyles.inlay.shadow}
      before:absolute before:inset-0 ${woodStyles.inlay.overlay}`}
    >
      {children}
    </div>
  );
};

export const ThemedButton = ({ children, onClick, disabled, variant = 'primary', className = '' }) => {
  const { woodStyles } = useTheme();

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-4 py-2 rounded-lg transition-colors duration-200 text-white font-medium
        ${disabled
          ? woodStyles.button.disabled
          : woodStyles.button[variant]} 
        ${className}`}
    >
      {children}
    </button>
  );
};

export const ThemedContainer = ({ children, className = '' }) => {
  const { woodStyles } = useTheme();

  return (
    <div
      className={`${className}`}
      style={{
        background: woodStyles.background,
        backgroundImage: woodStyles.backgroundImage,
        boxShadow: woodStyles.boxShadow
      }}
    >
      {children}
    </div>
  );
};

export const ThemeSwitch = () => {
  const { isDark, toggleTheme, woodStyles } = useTheme();

  return (
    <div className="flex items-center space-x-2">
      <span className={`text-sm ${woodStyles.text.primary}`}>☀️</span>
      <button
        onClick={toggleTheme}
        type="button"
        className={`
          relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full 
          border-2 border-transparent transition-colors duration-200 ease-in-out 
          focus:outline-none
          ${isDark ? woodStyles.button.primary : 'bg-gray-200'}
        `}
        role="switch"
        aria-checked={isDark}
      >
        <span
          aria-hidden="true"
          className={`
            pointer-events-none inline-block h-5 w-5 transform rounded-full 
            ${isDark ? 'bg-gray-900' : 'bg-white'} shadow ring-0 
            transition duration-200 ease-in-out
            ${isDark ? 'translate-x-5' : 'translate-x-0'}
          `}
        />
      </button>
      <span className={`text-sm ${woodStyles.text.primary}`}>🌙</span>
    </div>
  );
};

export default ThemeContext;